/**
 * ImportConfigModal (Phase Wellspring W1) — the ingest front-door.
 *
 * Opens when an operator imports a price sheet. It collects, before load: the
 * manufacturer (-> ingestion_batches.manufacturer_id + markup resolution), the
 * per-file markup override (defaults to the resolved (manufacturer, distributor)
 * junction markup), and the per-distributor column map — pre-filled from the
 * confirmed column_map_registry (or the heuristic suggester for a new distributor)
 * and REQUIRING an explicit cost-column confirmation before load (pipeline-plan §8;
 * the adapter must not auto-guess cost). On confirm it (re)seeds column_map_registry
 * (the cost row stamped cost_confirmed_at/by) and hands the manufacturer + markup
 * back to the caller's import.
 */
import { useState, useEffect, useCallback, type JSX } from 'react';
import * as XLSX from 'xlsx';
import { FormModal } from '../ui/FormModal';
import ArbiSelect from '../ui/ArbiSelect';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { detectSheets, type SheetMode } from '../../hooks/useImport';
import {
  buildColumnIndex,
  findHeaderRow,
  CANONICAL_FIELDS,
  canonicalFieldLabel,
} from '../../lib/algopix-columns';
import type { ToastState } from '../ui/Toast';

interface ImportFileRef {
  id: string;
  display_name: string | null;
  storage_path: string | null;
}

interface ImportConfigModalProps {
  open: boolean;
  file: ImportFileRef;
  distributorId: string | null;
  distributorName: string;
  onCancel: () => void;
  /** Caller runs the actual import (re-download + importFile + status update). */
  onConfirm: (
    manufacturerId: string,
    markupOverride: number | null,
    sheetMode: SheetMode,
    upstreamSourceText: string | null
  ) => void;
  showToast: (t: ToastState) => void;
}

const IGNORE = '';
const FIELD_OPTIONS = [
  { value: IGNORE, label: '— ignore —' },
  ...CANONICAL_FIELDS.map((f) => ({ value: f, label: canonicalFieldLabel(f) })),
];

export default function ImportConfigModal({
  open,
  file,
  distributorId,
  distributorName,
  onCancel,
  onConfirm,
  showToast,
}: ImportConfigModalProps): JSX.Element | null {
  const { user } = useApp();

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<string[]>([]); // parallel to headers; '' = ignore
  const [manufacturers, setManufacturers] = useState<Array<{ id: string; name: string }>>([]);
  const [manufacturerId, setManufacturerId] = useState<string>('');
  const [defaultMarkup, setDefaultMarkup] = useState<number | null>(null);
  const [markupOverride, setMarkupOverride] = useState<string>('');
  const [sheetMode, setSheetMode] = useState<SheetMode>('price_sheet');
  const [upstreamSourceText, setUpstreamSourceText] = useState<string>('');
  const [costConfirmed, setCostConfirmed] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Parse the sheet + load manufacturers + the existing registry on open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setManufacturerId('');
    setMarkupOverride('');
    setSheetMode('price_sheet');
    setUpstreamSourceText('');
    setDefaultMarkup(null);
    setCostConfirmed(false);

    (async () => {
      try {
        if (!distributorId) throw new Error('No active distributor — select one before importing.');
        if (!file.storage_path) throw new Error('No storage path — cannot read the file.');

        const { data: blob, error: dlErr } = await supabase.storage
          .from('arbi-files')
          .download(file.storage_path);
        if (dlErr) throw new Error(dlErr.message);
        if (!blob) throw new Error('Download returned empty payload.');

        const buffer = await blob.arrayBuffer();
        const sheetName = detectSheets(buffer)[0];
        const wb = XLSX.read(buffer, { type: 'array', cellDates: false });
        const sheet = wb.Sheets[sheetName];
        if (!sheet) throw new Error(`Sheet "${sheetName}" not found.`);
        const matrix = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: null,
          raw: true,
        }) as unknown[][];
        const hIdx = findHeaderRow(matrix);
        const hdrs = (matrix[hIdx] ?? [])
          .map((c) => (c == null ? '' : String(c).trim()))
          .filter((h) => h !== '');

        const [{ data: mfrs }, { data: regRows }] = await Promise.all([
          supabase.from('manufacturers').select('id, name').eq('is_active', true).order('name'),
          supabase
            .from('column_map_registry')
            .select('canonical_field, sheet_header, is_cost_column, cost_confirmed_at')
            .eq('distributor_id', distributorId),
        ]);
        if (cancelled) return;

        // Pre-fill: confirmed registry wins per header; else the heuristic suggester.
        const regByHeader = new Map<string, string>();
        let hasConfirmedCost = false;
        for (const r of regRows ?? []) {
          regByHeader.set(String(r.sheet_header).toLowerCase().trim(), r.canonical_field);
          if (r.is_cost_column && r.cost_confirmed_at) hasConfirmedCost = true;
        }
        const heuristic = buildColumnIndex(hdrs);
        const preMap = hdrs.map(
          (h, i) => regByHeader.get(h.toLowerCase().trim()) ?? heuristic[i] ?? IGNORE
        );

        setHeaders(hdrs);
        setMapping(preMap);
        setManufacturers((mfrs ?? []) as Array<{ id: string; name: string }>);
        // An established distributor's confirmed cost column carries the §8 gate
        // forward (re-confirmable); a new distributor must confirm.
        setCostConfirmed(hasConfirmedCost);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, distributorId, file.storage_path]);

  // Resolve the junction markup default when the manufacturer changes.
  const onPickManufacturer = useCallback(
    async (mfrId: string): Promise<void> => {
      setManufacturerId(mfrId);
      setDefaultMarkup(null);
      if (!mfrId || !distributorId) return;
      const { data } = await supabase
        .from('manufacturer_distributors')
        .select('markup_pct')
        .eq('manufacturer_id', mfrId)
        .eq('distributor_id', distributorId)
        .maybeSingle();
      setDefaultMarkup((data?.markup_pct as number | null | undefined) ?? null);
    },
    [distributorId]
  );

  const setFieldFor = (idx: number, field: string): void => {
    setMapping((prev) => prev.map((f, i) => (i === idx ? field : f)));
    if (field === 'cost_usd' || mapping[idx] === 'cost_usd') setCostConfirmed(false);
  };

  // Validation.
  const mapped = mapping.filter((f) => f !== IGNORE);
  const dupFields = mapped.filter((f, i) => mapped.indexOf(f) !== i);
  const costHeaderIdx = mapping.indexOf('cost_usd');
  const hasCost = costHeaderIdx !== -1;
  const overrideNum = markupOverride.trim() === '' ? null : Number(markupOverride);
  const overrideInvalid =
    markupOverride.trim() !== '' && (!Number.isFinite(overrideNum) || (overrideNum ?? -1) < 0);

  const canConfirm =
    !loading &&
    !loadError &&
    !saving &&
    // price_sheet sets the manufacturer once; supplier_sheet (clearance) resolves it per-row.
    (sheetMode === 'supplier_sheet' || manufacturerId !== '') &&
    hasCost &&
    costConfirmed &&
    dupFields.length === 0 &&
    !overrideInvalid;

  async function handleConfirm(): Promise<void> {
    if (!canConfirm || !distributorId) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const rows = headers
        .map((h, i) => ({ header: h, field: mapping[i] }))
        .filter((r) => r.field !== IGNORE)
        .map((r) => ({
          distributor_id: distributorId,
          canonical_field: r.field,
          sheet_header: r.header,
          is_cost_column: r.field === 'cost_usd',
          cost_confirmed_at: r.field === 'cost_usd' ? now : null,
          cost_confirmed_by: r.field === 'cost_usd' ? (user?.id ?? null) : null,
        }));

      // Upsert the confirmed rows, then prune fields no longer mapped — a partial
      // failure leaves a working-if-stale map rather than a wiped one (Sol, Phase 3).
      const { error: upErr } = await supabase
        .from('column_map_registry')
        .upsert(rows as never, { onConflict: 'distributor_id,canonical_field' });
      if (upErr) throw new Error(upErr.message);
      const keptFields = rows.map((r) => r.canonical_field);
      const { error: delErr } = await supabase
        .from('column_map_registry')
        .delete()
        .eq('distributor_id', distributorId)
        .not('canonical_field', 'in', `(${keptFields.join(',')})`);
      if (delErr) throw new Error(delErr.message);

      // supplier_sheet: no set-once manufacturer, markup forced 0 (clearance price IS the cost),
      // carry the upstream-source provenance free-text.
      onConfirm(
        sheetMode === 'supplier_sheet' ? '' : manufacturerId,
        sheetMode === 'supplier_sheet' ? 0 : overrideNum,
        sheetMode,
        sheetMode === 'supplier_sheet' ? upstreamSourceText.trim() || null : null
      );
    } catch (e) {
      showToast({
        kind: 'error',
        message: `Could not save the column map — ${e instanceof Error ? e.message : String(e)}`,
      });
      setSaving(false);
    }
  }

  if (!open) return null;

  const footer = (
    <>
      <button onClick={onCancel} disabled={saving} className="arbi-btn-ghost text-xs px-3 py-1.5">
        Cancel
      </button>
      <button
        onClick={() => void handleConfirm()}
        disabled={!canConfirm}
        className="arbi-btn text-xs bg-accent text-white px-3 py-1.5 disabled:opacity-50"
      >
        {saving ? 'Importing…' : 'Confirm & Import'}
      </button>
    </>
  );

  return (
    <FormModal
      open={open}
      title="Configure import"
      subtitle={file.display_name ?? undefined}
      size="3xl"
      saving={saving}
      onClose={onCancel}
      footer={footer}
    >
      {loading ? (
        <p className="py-8 text-center text-sm text-ink-muted">Reading the sheet…</p>
      ) : loadError ? (
        <p className="py-8 text-center text-sm text-danger">{loadError}</p>
      ) : (
        <div className="space-y-5">
          {/* Sheet type (K5.2a) */}
          <div>
            <span className="mb-1 block text-2xs uppercase tracking-wider text-ink-muted">
              Sheet type
            </span>
            <div className="inline-flex overflow-hidden rounded-md border border-border text-xs">
              <button
                type="button"
                onClick={() => setSheetMode('price_sheet')}
                className={`px-3 py-1.5 ${sheetMode === 'price_sheet' ? 'bg-accent text-white' : 'bg-surface-2 text-ink-secondary'}`}
              >
                Price sheet
              </button>
              <button
                type="button"
                onClick={() => setSheetMode('supplier_sheet')}
                className={`border-l border-border px-3 py-1.5 ${sheetMode === 'supplier_sheet' ? 'bg-accent text-white' : 'bg-surface-2 text-ink-secondary'}`}
              >
                Supplier sheet (clearance)
              </button>
            </div>
            <p className="mt-1 text-2xs text-ink-muted">
              {sheetMode === 'price_sheet'
                ? 'Standing sheet — one manufacturer, markup applied; existing products update in place.'
                : 'Clearance / lot — mixed manufacturers resolved per-row; the typed price is the cost (no markup). Matches attach as lots; cryptic rows hold for review.'}
            </p>
          </div>

          {/* Distributor + manufacturer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="mb-1 block text-2xs uppercase tracking-wider text-ink-muted">
                Distributor
              </span>
              <div className="rounded-md border border-border bg-surface-2 px-3 py-1.5 text-xs text-ink-secondary">
                {distributorName || '—'}
              </div>
            </div>
            <div>
              <span className="mb-1 block text-2xs uppercase tracking-wider text-ink-muted">
                Manufacturer {sheetMode === 'price_sheet' && <span className="text-danger">*</span>}
              </span>
              {sheetMode === 'supplier_sheet' ? (
                <div className="rounded-md border border-border bg-surface-2 px-3 py-1.5 text-xs text-ink-muted">
                  Resolved per-row (mixed)
                </div>
              ) : (
                <ArbiSelect
                  value={manufacturerId}
                  onChange={(v) => void onPickManufacturer(String(v))}
                  options={manufacturers.map((m) => ({ value: m.id, label: m.name }))}
                  placeholder="Select manufacturer…"
                  portal
                />
              )}
            </div>
          </div>

          {/* Markup (price_sheet) / upstream-source provenance (supplier_sheet) */}
          {sheetMode === 'price_sheet' ? (
            <div>
              <span className="mb-1 block text-2xs uppercase tracking-wider text-ink-muted">
                Markup override (base → cost)
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  value={markupOverride}
                  onChange={(e) => setMarkupOverride(e.target.value)}
                  aria-label="Markup percent override"
                  placeholder={defaultMarkup != null ? defaultMarkup.toFixed(2) : '0.00'}
                  className="arbi-input w-24 px-2 py-0.5 text-right font-mono text-xs"
                />
                <span className="text-xs text-ink-muted">%</span>
                <span className="text-2xs text-ink-muted">
                  {manufacturerId
                    ? `Default from terms: ${(defaultMarkup ?? 0).toFixed(2)}% — leave blank to use it.`
                    : 'Select a manufacturer to resolve the default.'}
                </span>
              </div>
              {overrideInvalid && (
                <p className="mt-1 text-2xs text-danger">Markup must be a number ≥ 0.</p>
              )}
            </div>
          ) : (
            <div>
              <span className="mb-1 block text-2xs uppercase tracking-wider text-ink-muted">
                Upstream source (provenance)
              </span>
              <input
                type="text"
                value={upstreamSourceText}
                onChange={(e) => setUpstreamSourceText(e.target.value)}
                aria-label="Upstream source"
                placeholder="e.g. the wholesaler this clearance is fronted from (optional)"
                className="arbi-input w-full px-2 py-1 text-xs"
              />
              <p className="mt-1 text-2xs text-ink-muted">
                Free-text record only — never participates in cost or resolution. Markup is not
                applied: the clearance price you map IS the cost.
              </p>
            </div>
          )}

          {/* Column map */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-2xs uppercase tracking-wider text-ink-muted">
                Column map ({headers.length} columns)
              </span>
              {dupFields.length > 0 && (
                <span className="text-2xs text-danger">
                  Each field maps once — duplicate: {dupFields.map(canonicalFieldLabel).join(', ')}
                </span>
              )}
            </div>
            <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-border-subtle p-2">
              {headers.map((h, i) => (
                <div
                  key={`${h}-${i}`}
                  className={`flex items-center gap-3 rounded px-2 py-1 ${
                    mapping[i] === 'cost_usd' ? 'bg-accent/10' : ''
                  }`}
                >
                  <span className="flex-1 truncate font-mono text-2xs text-ink-secondary" title={h}>
                    {h}
                  </span>
                  <div className="w-56 shrink-0">
                    <ArbiSelect
                      value={mapping[i] ?? IGNORE}
                      onChange={(v) => setFieldFor(i, String(v))}
                      options={FIELD_OPTIONS}
                      size="xs"
                      portal
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cost-column confirm gate (§8) */}
          <div className="rounded-md border border-border bg-surface-2 px-3 py-2">
            {hasCost ? (
              <label className="flex items-center gap-2 text-xs text-ink-secondary">
                <input
                  type="checkbox"
                  checked={costConfirmed}
                  onChange={(e) => setCostConfirmed(e.target.checked)}
                  className="accent-accent"
                />
                <span>
                  I confirm the cost column is{' '}
                  <span className="font-mono text-ink-bright">{headers[costHeaderIdx]}</span> — its
                  values become <span className="font-mono">cost_usd</span>{' '}
                  {sheetMode === 'supplier_sheet' ? '(the clearance price).' : '(after markup).'}
                </span>
              </label>
            ) : (
              <p className="text-xs text-danger">
                Map one column to <span className="font-mono">Cost (base, USD)</span> to enable
                import — the adapter never guesses cost.
              </p>
            )}
          </div>
        </div>
      )}
    </FormModal>
  );
}
